import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiGet } from "./client";
import { useTicketSize } from "../ticket-size";
import type { ThreeWayBasis } from "../../../../components/PortfolioPerformance/basis-toggle";
import {
  cohortColor,
  type CohortYear,
  type MonthRow,
} from "./lifetime-loss-projections";

export { cohortColor };
export type { CohortYear };

export interface LifetimeRepeatCustomerProjectionsSummary {
  /** 48-month repeat rate at the latest file date; null while repeat_customers_summary has no figure. */
  historicalRepeatRatePct: number | null;
  /** change vs the previous file date */
  trend: { value: string; direction: "up" | "down" | "flat" } | null;
  sourceNote: string;
  cohorts: string[];
  /** cohort year -> month labels that are realized readings (the rest of its curve is projected) */
  realized: Record<string, string[]>;
  forecast: MonthRow[];
  projection: MonthRow[];
}

// null only when the selected industry has no rows in the chart table -- render an empty
// state, not a fabricated chart. The toggle picks the rows that are realized, projected, or of active cohorts.
export function useLifetimeRepeatCustomerProjections(industry: string, basis: ThreeWayBasis) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["lifetime-repeat-customer-projections", industry, basis, ticket ?? "all"],
    queryFn: () =>
      apiGet<LifetimeRepeatCustomerProjectionsSummary | null>(
        "/lifetime-repeat-customer-projections",
        { industry, ticket, basis },
      ),
    placeholderData: keepPreviousData,
  });
}

export interface RepeatCustomerPoint {
  month: string;
  value: number;
}

/** The page's card and two charts for the selected asset industry, borrower industry and ticket size. */
export interface RepeatCustomerSummary {
  asOf: string | null;
  /** SUM(repeat_48_mo) / SUM(trailing_12m_financed_amount) at the latest file date, as a % ("28.3%") */
  value: string;
  trend: { value: string; direction: "up" | "down" | "flat" };
  /** Booking Contribution by Repeat Customers (Trailing 12 Months): that ratio at every file date */
  contribution: RepeatCustomerPoint[];
  /** Repeat Customer Trends: month = 12 / 24 / 36 / 48, the repeat share at that window, latest file date */
  trends: RepeatCustomerPoint[];
  /** false while repeat_customers_summary has no industry columns */
  industryFilters: boolean;
}

// null while repeat_customers_summary is missing here.
export function useRepeatCustomerSummary(assetIndustry: string | undefined, borrowerIndustry: string | undefined) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["repeat-customer-summary", assetIndustry ?? null, borrowerIndustry ?? null, ticket ?? "all"],
    queryFn: () =>
      apiGet<RepeatCustomerSummary | null>("/repeat-customer-summary", {
        assetIndustry,
        borrowerIndustry,
        ticket,
      }),
    placeholderData: keepPreviousData,
  });
}
